import React, { useEffect, useMemo, useState } from "react";
import { Link2, Link2Off, Loader2, Plus, RefreshCw, Trash2, Upload } from "lucide-react";
import toast from "react-hot-toast";
import { payrollOpsService } from "../../services/api";
import { formatInr } from "./payroll/runPayrollUtils";
import LinkDeductionsModal from "./LinkDeductionsModal";
import AddChallanModal, { emptyChallan } from "./AddChallanModal";

const STATUS_BADGE = {
  UNUSED: "bg-red-100 text-red-800",
  PARTIAL: "bg-amber-100 text-amber-900",
  FULL: "bg-emerald-100 text-emerald-800",
  CANCELLED: "bg-gray-200 text-gray-600",
};

/**
 * Form 24Q Step 3 — TDS Challan Management:
 * register, modal entry, balances, period-aware Link / Unlink.
 */
export default function Form24QChallanPanel({
  filingId,
  financialYear,
  quarter,
  darkMode = false,
  onBundleChange,
  legacyChallan,
  onLegacyChallanChange,
}) {
  const formatPeriod = (value) => {
    if (!value) return "—";
    const [yy, mm] = String(value).split("-");
    if (!yy || !mm) return value;
    return `${mm}-${yy}`;
  };

  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [csiText, setCsiText] = useState("");
  const [linkChallan, setLinkChallan] = useState(null);
  const [consumptions, setConsumptions] = useState([]);
  const [unlinkingId, setUnlinkingId] = useState(null);
  const [selectedLinkIds, setSelectedLinkIds] = useState([]);

  const inputCls = `mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
    darkMode ? "border-[#555] bg-[#2a2a2a] text-white" : "border-gray-300 bg-white"
  }`;
  const cardCls = `rounded-xl border ${darkMode ? "border-[#444] bg-[#242424]" : "border-gray-200 bg-white"}`;

  const load = async () => {
    if (!filingId) return;
    setLoading(true);
    try {
      const [bundleRes, consRes] = await Promise.all([
        payrollOpsService.getForm24QFilingBundle(filingId),
        payrollOpsService.getTdsChallanConsumptions({ filingId }).catch(() => null),
      ]);
      const data = bundleRes?.data || bundleRes;
      setBundle(data);
      onBundleChange?.(data);
      setConsumptions(consRes?.data || consRes || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Unable to load challans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filingId]);

  const challans = bundle?.challans || [];
  const recon = bundle?.reconciliation;
  const employeeOptions = bundle?.employees || [];

  const employeeLookup = useMemo(() => {
    const byId = {};
    for (const emp of employeeOptions) {
      if (!emp?.id) continue;
      const user = emp.user || {};
      const empCode = emp.empCode || emp.employeeCode || user.empCode || "—";
      const name =
        [
          user.name,
          [user.firstName, user.lastName].filter(Boolean).join(" ").trim(),
          emp.name,
        ].find((v) => v && String(v).trim()) || "—";
      byId[Number(emp.id)] = { empCode, name };
    }
    return byId;
  }, [employeeOptions]);

  const summary = useMemo(() => {
    let totalPaid = 0;
    let totalDeposited = 0;
    let totalConsumed = 0;
    let totalBalance = 0;
    let unused = 0;
    let partial = 0;
    let full = 0;
    for (const c of challans) {
      const tds = Number(c.tdsAmount || 0);
      totalPaid += tds;
      totalDeposited += Number(c.totalAmountDeposited || tds || 0);
      totalConsumed += Number(c.amountConsumed || 0);
      const bal = Number(
        c.balanceAvailable != null
          ? c.balanceAvailable
          : tds - Number(c.amountConsumed || 0)
      );
      totalBalance += bal;
      const st =
        c.challanStatus ||
        (bal <= 0.01 ? "FULL" : Number(c.amountConsumed || 0) > 0 ? "PARTIAL" : "UNUSED");
      if (st === "UNUSED") unused += 1;
      else if (st === "PARTIAL") partial += 1;
      else if (st === "FULL") full += 1;
    }
    return {
      totalPaid: Math.round(totalPaid * 100) / 100,
      totalDeposited: Math.round(totalDeposited * 100) / 100,
      totalConsumed: Math.round(totalConsumed * 100) / 100,
      totalBalance: Math.round(totalBalance * 100) / 100,
      unused,
      partial,
      full,
      deducteeTds: recon?.totalDeducteeTds || 0,
      mapped: recon?.totalMapped || 0,
    };
  }, [challans, recon]);

  const openAdd = () => {
    setDraft(emptyChallan());
    setShowModal(true);
  };

  const openEdit = (c) => {
    setDraft({
      id: c.id,
      bsrCode: c.bsrCode || "",
      challanSerialNumber: c.challanSerialNumber || "",
      cin: c.cin || "",
      dateOfDeposit: c.dateOfDeposit ? String(c.dateOfDeposit).slice(0, 10) : "",
      totalAmountDeposited: String(c.totalAmountDeposited ?? ""),
      tdsAmount: String(c.tdsAmount ?? ""),
      surchargeAmount: String(c.surchargeAmount ?? 0),
      cessAmount: String(c.cessAmount ?? 0),
      interestAmount: String(c.interestAmount ?? 0),
      othersAmount: String(c.othersAmount ?? 0),
      fee234EAmount: String(c.fee234EAmount ?? 0),
      isNilChallan: !!c.isNilChallan,
      bankName: c.bankName || "",
      paymentMode: c.paymentMode || "E-Payment",
      remarks: c.remarks || "",
    });
    setShowModal(true);
  };

  const saveChallan = async (formDraft) => {
    if (!filingId) {
      toast.error("Prepare the return first (step 1)");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formDraft,
        financialYear,
        quarter,
        formType: "24Q",
        section: "192",
        amountBreakup: {
          tds: formDraft.tdsAmount || formDraft.totalAmountDeposited,
          surcharge: formDraft.surchargeAmount,
          cess: formDraft.cessAmount,
          interest: formDraft.interestAmount,
          others: formDraft.othersAmount,
          fee234E: formDraft.fee234EAmount,
        },
      };
      const res = await payrollOpsService.upsertForm24QChallan(filingId, payload, formDraft.id);
      const nextBundle = res?.data?.bundle || res?.bundle;
      if (nextBundle) {
        setBundle(nextBundle);
        onBundleChange?.(nextBundle);
      } else {
        await load();
      }
      setShowModal(false);
      setDraft(null);
      toast.success(
        formDraft.id
          ? "Challan updated"
          : "Challan saved — use Link on the row to map a period’s deductions"
      );
      if (onLegacyChallanChange) {
        onLegacyChallanChange({
          ...legacyChallan,
          challanNumber: payload.challanSerialNumber,
          challanDate: payload.dateOfDeposit,
          bsrCode: payload.bsrCode,
          bankName: payload.bankName,
          challanAmount: String(payload.totalAmountDeposited || payload.tdsAmount || ""),
          interestAmount: String(payload.interestAmount || "0"),
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeChallan = async (id) => {
    if (!window.confirm("Delete this challan and its mappings/links?")) return;
    setLoading(true);
    try {
      const res = await payrollOpsService.deleteForm24QChallan(filingId, id);
      setBundle(res?.data || res);
      onBundleChange?.(res?.data || res);
      toast.success("Challan deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const unlinkConsumption = async (row) => {
    if (row.isReversed) return;
    const ok = window.confirm(
      `Unlink ${formatInr(row.consumedAmount)} for ${row.payMonth || "this period"} from challan #${row.challanId}?`
    );
    if (!ok) return;
    setUnlinkingId(row.id);
    try {
      await payrollOpsService.reverseTdsChallanConsumption(row.id, "Unlinked from Form 24Q");
      toast.success("Unlinked — challan balance restored");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Unlink failed");
    } finally {
      setUnlinkingId(null);
    }
  };

  const toggleSelectedLink = (id) => {
    setSelectedLinkIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  const toggleAllActiveLinks = (rows, checked) => {
    if (!checked) {
      setSelectedLinkIds([]);
      return;
    }
    setSelectedLinkIds(rows.map((r) => r.id));
  };

  const unlinkSelectedLinks = async (rows) => {
    const picked = rows.filter((r) => selectedLinkIds.includes(r.id));
    if (!picked.length) {
      toast.error("Select at least one linked row");
      return;
    }
    const total = picked.reduce((sum, r) => sum + Number(r.consumedAmount || 0), 0);
    const ok = window.confirm(
      `Unlink ${picked.length} row(s) totaling ${formatInr(total)}?`
    );
    if (!ok) return;
    setLoading(true);
    try {
      for (const row of picked) {
        await payrollOpsService.reverseTdsChallanConsumption(row.id, "Bulk unlink from Form 24Q");
      }
      toast.success(`${picked.length} link(s) unlinked`);
      setSelectedLinkIds([]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Bulk unlink failed");
    } finally {
      setLoading(false);
    }
  };

  const importCsi = async () => {
    if (!csiText.trim()) {
      toast.error("Paste CSI file contents first");
      return;
    }
    setLoading(true);
    try {
      const res = await payrollOpsService.importForm24QCsi(filingId, {
        content: csiText,
        fileName: "pasted.csi",
      });
      setBundle(res?.data?.bundle || res?.bundle);
      onBundleChange?.(res?.data?.bundle);
      toast.success(
        `CSI imported — ${res?.data?.matched ?? 0} / ${res?.data?.parsedCount ?? 0} matched`
      );
    } catch (e) {
      toast.error(e?.response?.data?.message || "CSI import failed");
    } finally {
      setLoading(false);
    }
  };

  if (!filingId) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        Prepare the return in <strong>Step 1</strong> first, then return here to add challans and{" "}
        <strong>Link</strong> month-wise deductions.
      </div>
    );
  }

  const activeLinks = (consumptions || []).filter((c) => !c.isReversed);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">TDS Challan Management</h3>
          <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
            Add ITNS 281 challans for {financialYear} {quarter}, then <strong>Link</strong> a
            specific quarter/month’s deductions. Use <strong>Unlink</strong> to reverse a mapping.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            className={`inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs ${
              darkMode ? "border-[#555]" : "border-gray-300"
            }`}
          >
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex items-center gap-1 rounded-lg bg-redhalo-red px-3 py-1.5 text-xs font-medium text-white"
          >
            <Plus size={12} /> Add challan
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {[
          ["Challans TDS paid", formatInr(summary.totalPaid)],
          ["Utilized / linked", formatInr(summary.totalConsumed || summary.mapped)],
          ["Balance available", formatInr(summary.totalBalance)],
          ["Deductee TDS (Q)", formatInr(summary.deducteeTds)],
        ].map(([label, value]) => (
          <div key={label} className={`${cardCls} p-3`}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
            <p className="mt-1 text-sm font-bold tabular-nums">{value}</p>
            {label === "Challans TDS paid" &&
              Math.abs(summary.totalDeposited - summary.totalPaid) > 0.01 && (
                <p className="mt-0.5 text-[10px] text-gray-400">
                  Total deposited (incl. interest/fee): {formatInr(summary.totalDeposited)}
                </p>
              )}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 text-[11px]">
        <span className={`rounded-full px-2 py-0.5 font-semibold ${STATUS_BADGE.UNUSED}`}>
          {summary.unused} Unused
        </span>
        <span className={`rounded-full px-2 py-0.5 font-semibold ${STATUS_BADGE.PARTIAL}`}>
          {summary.partial} Partial
        </span>
        <span className={`rounded-full px-2 py-0.5 font-semibold ${STATUS_BADGE.FULL}`}>
          {summary.full} Fully linked
        </span>
        {recon && (
          <span
            className={`rounded-full px-2 py-0.5 font-semibold ${
              recon.balanced ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"
            }`}
          >
            {recon.balanced ? "Reconciliation OK" : "Still unbalanced — finish Link"}
          </span>
        )}
      </div>

      <div className={`${cardCls} overflow-hidden`}>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Challan register
          </h4>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-redhalo-red"
          >
            <Plus size={12} /> Add challan
          </button>
        </div>
        <div className="max-h-[min(40vh,360px)] overflow-auto">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 z-10 bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))] shadow-[0_1px_0_hsl(var(--ds-border))]">
              <tr>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">BSR / Serial</th>
                <th className="px-3 py-2 text-right">TDS</th>
                <th className="px-3 py-2 text-right">Consumed</th>
                <th className="px-3 py-2 text-right">Balance</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {challans.map((c) => {
                const bal = Number(
                  c.balanceAvailable != null
                    ? c.balanceAvailable
                    : Number(c.tdsAmount || 0) - Number(c.amountConsumed || 0)
                );
                const st =
                  c.challanStatus ||
                  (bal <= 0.01 ? "FULL" : Number(c.amountConsumed || 0) > 0 ? "PARTIAL" : "UNUSED");
                return (
                  <tr
                    key={c.id}
                    className={`border-t ${darkMode ? "border-[#333]" : "border-gray-100"}`}
                  >
                    <td className="px-3 py-2">{c.dateOfDeposit || "—"}</td>
                    <td className="px-3 py-2 font-mono">
                      {c.bsrCode || "—"} / {c.challanSerialNumber || "—"}
                      {c.isNilChallan && (
                        <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-800">
                          Nil
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatInr(c.tdsAmount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatInr(c.amountConsumed || 0)}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {formatInr(bal)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                          STATUS_BADGE[st] || STATUS_BADGE.UNUSED
                        }`}
                      >
                        {st}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() =>
                            setLinkChallan({ ...c, balanceAvailable: bal, challanStatus: st })
                          }
                          disabled={bal <= 0.01 && st === "FULL"}
                          className="inline-flex items-center gap-1 rounded-lg bg-redhalo-red px-2.5 py-1 text-[11px] font-bold text-white shadow-sm disabled:opacity-40"
                          title="Link deductions for a selected period"
                        >
                          <Link2 size={12} /> Link
                        </button>
                        <button
                          type="button"
                          className="rounded border px-2 py-1 text-[11px]"
                          onClick={() => openEdit(c)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => removeChallan(c.id)}
                          className="rounded p-1 text-red-600"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!challans.length && (
                <tr>
                  <td colSpan={7} className="px-3 py-10 text-center text-gray-400">
                    <p className="mb-2">No challans yet</p>
                    <button
                      type="button"
                      onClick={openAdd}
                      className="inline-flex items-center gap-1 rounded-lg bg-redhalo-red px-3 py-1.5 text-xs font-medium text-white"
                    >
                      <Plus size={12} /> Add challan
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Link / utilization history */}
      <div className={`${cardCls} overflow-hidden`}>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Link / utilization history (this return)
          </h4>
          {!!activeLinks.length && (
            <button
              type="button"
              onClick={() => unlinkSelectedLinks(activeLinks)}
              disabled={!selectedLinkIds.length || loading}
              className="inline-flex items-center gap-1 rounded border border-red-200 px-2.5 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              <Link2Off size={12} /> Unlink selected ({selectedLinkIds.length})
            </button>
          )}
        </div>
        {!activeLinks.length ? (
          <p className="px-4 py-6 text-center text-xs text-gray-400">
            No links yet. Use <strong>Link</strong> on a challan and choose the quarter/month first.
          </p>
        ) : (
          <div className="max-h-[min(40vh,360px)] overflow-auto">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 z-10 bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))] shadow-[0_1px_0_hsl(var(--ds-border))]">
                <tr>
                  <th className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={
                        activeLinks.length > 0 &&
                        activeLinks.every((r) => selectedLinkIds.includes(r.id))
                      }
                      onChange={(e) => toggleAllActiveLinks(activeLinks, e.target.checked)}
                    />
                  </th>
                  <th className="px-3 py-2">Challan</th>
                  <th className="px-3 py-2">Period</th>
                  <th className="px-3 py-2">Employee Code</th>
                  <th className="px-3 py-2">Employee Name</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {activeLinks.map((c) => {
                  const emp = employeeLookup[Number(c.employeeId)] || {};
                  const empCode = c.empCode || emp.empCode || "—";
                  const empName = c.employeeName || c.deducteeName || emp.name || "—";
                  return (
                  <tr
                    key={c.id}
                    className={`border-t ${darkMode ? "border-[#333]" : "border-gray-100"}`}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedLinkIds.includes(c.id)}
                        onChange={() => toggleSelectedLink(c.id)}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono">#{c.challanId}</td>
                    <td className="px-3 py-2">{formatPeriod(c.payMonth)}</td>
                    <td className="px-3 py-2 font-mono">{empCode}</td>
                    <td className="px-3 py-2">{empName}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">
                      {formatInr(c.consumedAmount)}
                    </td>
                    <td className="px-3 py-2">
                      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800">
                        Linked
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        disabled={unlinkingId === c.id}
                        onClick={() => unlinkConsumption(c)}
                        className="inline-flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                        title="Unlink this mapping"
                      >
                        {unlinkingId === c.id ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Link2Off size={12} />
                        )}
                        Unlink
                      </button>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={`${cardCls} p-4`}>
        <h4 className="mb-1 text-sm font-semibold">Import .csi (OLTAS)</h4>
        <div className="mb-3 space-y-1.5 rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-3 text-[11px] text-[hsl(var(--ds-muted-fg))]">
          <p className="font-semibold text-[hsl(var(--ds-fg))]">How to use CSI import</p>
          <ol className="list-decimal space-y-1 pl-4">
            <li>Download the <strong>.csi</strong> file from TIN-NSDL / OLTAS (challan status inquiry) for your TAN.</li>
            <li>Open it in Notepad and copy the full contents.</li>
            <li>Paste below and click <strong>Import CSI</strong>.</li>
            <li>
              PaceHRM matches rows by <strong>BSR code + challan serial</strong> against your register and sets
              OLTAS status to MATCHED / UNMATCHED.
            </li>
          </ol>
          <p className="pt-1">
            Useful to confirm bank deposits match what you entered before generating RPU / FVU.
            Imported CSI is also passed to the official FVU jar on <strong>Run official FVU</strong>.
            If you skip import, PaceHRM synthesizes a CSI file from your challan register.
          </p>
          <p className="font-mono text-[10px]">
            Expected line format: BSR^SERIAL^YYYY-MM-DD^AMOUNT[^TDS] (or DDMMYYYY)
          </p>
        </div>
        <textarea
          className={`${inputCls} min-h-[72px] font-mono text-xs`}
          value={csiText}
          onChange={(e) => setCsiText(e.target.value)}
          placeholder="BSRCODE^CHALLANNO^2026-04-07^150000^…"
        />
        <button
          type="button"
          onClick={importCsi}
          className="mt-2 inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm"
        >
          <Upload size={14} /> Import CSI
        </button>
        {(bundle?.filing?.csiFileName || bundle?.filing?.csiImportedAt) && (
          <p className="mt-2 text-[11px] text-emerald-700">
            CSI ready for FVU
            {bundle?.filing?.csiFileName ? `: ${bundle.filing.csiFileName}` : ""}
            {bundle?.filing?.csiImportedAt
              ? ` · imported ${new Date(bundle.filing.csiImportedAt).toLocaleString("en-IN")}`
              : ""}
          </p>
        )}
      </div>

      {(recon?.errors || []).length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-900">
          <p className="font-semibold">Finish linking before RPU</p>
          <p className="mt-1 text-[11px] text-red-800/90">
            Matching total challan TDS to total deductee TDS is not enough — each deductee must be
            fully linked. Use <strong>Link</strong> on a challan that still has balance.
          </p>
          {recon.errors.slice(0, 6).map((e, i) => (
            <p key={i} className="mt-0.5">
              • {e.message}
            </p>
          ))}
        </div>
      )}

      <AddChallanModal
        open={showModal}
        onClose={() => {
          if (!saving) {
            setShowModal(false);
            setDraft(null);
          }
        }}
        darkMode={darkMode}
        initial={draft}
        financialYear={financialYear}
        quarter={quarter}
        saving={saving}
        onSave={saveChallan}
      />

      <LinkDeductionsModal
        open={!!linkChallan}
        onClose={() => setLinkChallan(null)}
        darkMode={darkMode}
        challan={linkChallan}
        filingId={filingId}
        financialYear={financialYear}
        quarter={quarter}
        onLinked={async (data) => {
          if (data?.bundle) {
            setBundle(data.bundle);
            onBundleChange?.(data.bundle);
          }
          await load();
        }}
      />
    </div>
  );
}
